import { Color } from '@diritto/common/style/color';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { isPC } from 'constants/layout';
import { ModalParams, ModalType } from 'constants/modal';
import { CloseIcon } from 'icons/CloseIcon';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getCoverImageBorder, getCoverImageStyle } from 'styles/common';
import { IconButton } from 'ui-components/common/IconButton';
import { IkImage } from 'ui-components/Imagekit/IkImage';
import { ModalLayout } from 'ui-components/layout/ModalLayout';
import { ViewPortHeight } from 'ui-components/layout/styles';
import { useShowModal } from 'ui-hooks/useShowModal';
import { useViewPort } from 'ui-hooks/useViewPort';
import { ImageRatio } from '@diritto/common/base/constants';
import { ModalHandler } from 'utils/ModalHandler';

export const ContentCoverModal = () => {
  const viewportSize = useViewPort();
  const { isShow, revealModal, showModal, hideModal } = useShowModal();
  const [state, setState] = useState<ModalParams[ModalType.ContentCover]>();

  const handleClose = useCallback(() => {
    hideModal();
  }, [hideModal]);

  useEffect(() => {
    const listener = (show: boolean, params?: ModalParams[ModalType.ContentCover]) => {
      if (show) {
        showModal();
      } else {
        hideModal();
      }
      setState(params);
    };

    ModalHandler.addEventListener(ModalType.ContentCover, listener);

    return () => {
      ModalHandler.removeEventListener(ModalType.ContentCover);
    };
  }, [hideModal, showModal]);

  const size = useMemo(() => {
    if (viewportSize.width * ImageRatio < viewportSize.height) {
      return { width: viewportSize.width, height: viewportSize.width * ImageRatio };
    }
    return { width: viewportSize.height / ImageRatio, height: viewportSize.height };
  }, [viewportSize.height, viewportSize.width]);

  return (
    <ModalLayout isShow={isShow} revealModal={revealModal} onClick={handleClose}>
      <StyledModal width={size.width} height={size.height}>
        <StyledIconButton onClick={handleClose}>
          <CloseIcon color={Color.White} />
        </StyledIconButton>
        <StyledImageWrapper>
          {state?.imageFile && (
            <IkImage
              transform={{
                path: state.imageFile.path,
                isPrivate: state.imageFile.isPrivate,
              }}
              alt="cover"
            />
          )}
        </StyledImageWrapper>
      </StyledModal>
    </ModalLayout>
  );
};

const StyledIconButton = styled(IconButton)`
  margin: 0 0 auto auto;
`;

const StyledModal = styled.div(
  ({ width, height }: { width: number; height: number }) => css`
    width: 100vw;
    ${ViewPortHeight(height)};
    text-align: center;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 20px ${20 / ImageRatio}px;

    @media (${isPC}) {
      width: ${width}px;
    }
  `,
);

const StyledImageWrapper = styled.div`
  align-self: center;
  margin: 0 auto auto;
  height: 100%;
  width: 100%;

  ${getCoverImageBorder()};

  & img {
    ${getCoverImageStyle()}
  }
`;
